import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const TARGET_ID = "SPdqELFz2ovWSn8eFyws";

function normalizeText(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/\s+/g, " ")
    .trim();
}

function getCurrentSchoolYearAge(dateOfBirth) {
  if (!dateOfBirth || typeof dateOfBirth !== "string") return null;

  const year = Number(String(dateOfBirth).split("-")[0]);
  if (!year || Number.isNaN(year)) return null;

  const currentYear = new Date().getFullYear();
  return currentYear - year;
}

function detectTypeFromGroup(group) {
  const raw = [
    group?.name,
    group?.type,
    group?.custom_name,
    group?.days?.[0]?.custom_name,
  ]
    .filter(Boolean)
    .join(" | ");

  const text = normalizeText(raw);

  if (!text) return "inne";

  // turniejówki
  if (
    text.includes("mini stars") ||
    text.includes("super stars") ||
    text.includes("stars ii") ||
    text.includes("stars 2") ||
    text.includes("stars") ||
    text.includes("arytmia")
  ) {
    return "turniejowka";
  }

  // osobny typ łączony
  if (
    text.includes("aerial hoop/hammock") ||
    text.includes("hoop/hammock") ||
    text.includes("kola/hamaki") ||
    text.includes("kolo/hamak") ||
    text.includes("kola i hamaki")
  ) {
    return "aerial hoop/hammock";
  }

  if (text.includes("aerial hammock") || text.includes("hammock") || text.includes("hamak")) {
    return "aerial hammock";
  }

  if (
    text.includes("aerial hoop") ||
    text.includes("hoop") ||
    text.includes("kolo") ||
    text.includes("kola")
  ) {
    return "aerial hoop";
  }

  if (text.includes("aerial silk") || text.includes("silk") || text.includes("szarfa") || text.includes("szarfy")) {
    return "aerial silk";
  }

  // ogólnorozwojowe liczymy do akrobatyki
  if (text.includes("ogolnorozwojowe") || text.includes("zajecia ogolnorozwojowe")) {
    return "akrobatyka";
  }

  if (text.includes("akrobat")) {
    return "akrobatyka";
  }

  if (text.includes("gimnastyka artystyczna")) {
    return "akrobatyka";
  }

  if (text.includes("acrodance")) {
    return "taniec";
  }

  if (text.includes("mix dance")) {
    return "taniec";
  }

  if (text.includes("modern jazz") || text.includes("modern")) {
    return "taniec";
  }

  if (text.includes("balet")) {
    return "taniec";
  }

  if (text.includes("high heels")) {
    return "taniec";
  }

  if (text.includes("latino")) {
    return "taniec";
  }

  if (text.includes("pole dance") || text.includes("pole")) {
    return "pole dance";
  }

  if (text.includes("taniec towarzyski")) {
    return "taniec towarzyski";
  }

  if (text.includes("zdrowie i mobilnosc") || text.includes("mobilnosc")) {
    return "zdrowie i mobilność";
  }

  if (text.includes("taniec nowoczesny")) {
    return "taniec";
  }

  if (text.includes("taniec")) {
    return "taniec";
  }

    if (text.includes("k-pop")) {
    return "taniec";
  }

      if (text.includes("K-Pop")) {
    return "taniec";
  }

        if (text.includes("K Pop")) {
    return "taniec";
  }

  return "inne";
}

export default function AgeTypeStats({ isToogle }) {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "users2026"),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const clients = [];

        for (let i = 0; i < data.length; i++) {
          const subUsers = Object.values(data[i]?.subUsers || {});
          for (let x = 0; x < subUsers.length; x++) {
            clients.push({
              uid: data[i]?.uid || data[i]?.id || snapshot.docs[i]?.id,
              acc: data[i],
              user: subUsers[x],
            });
          }
        }

        setUsers(clients);
        setLoadingUsers(false);
      },
      (err) => {
        console.error(err);
        setUsers([]);
        setLoadingUsers(false);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const ref = doc(db, "groups", TARGET_ID);

    const unsub = onSnapshot(
      ref,
      (snapshot) => {
        if (!snapshot.exists()) {
          setGroups([]);
          setLoadingGroups(false);
          return;
        }

        const data = snapshot.data() || {};
        const groupsArr = Object.entries(data)
          .map(([id, value]) => ({
            id,
            ...value,
          }))
          .filter(Boolean);

        setGroups(groupsArr);
        setLoadingGroups(false);
      },
      (err) => {
        console.error(err);
        setGroups([]);
        setLoadingGroups(false);
      }
    );

    return () => unsub();
  }, []);

  const groupIndex = useMemo(() => {
    return new Map((groups || []).map((g) => [g?.id, g]));
  }, [groups]);

const stats = useMemo(() => {
  if (!users?.length || !groups?.length) {
    return {
      rows: [],
      grouped: {},
      totalParticipants: 0,
      countedPairs: 0,
    };
  }

  const ageTypeMap = new Map();
  const groupedParticipants = {};
  const globalUniqueParticipants = new Set();

  users.forEach((entry) => {
    const participant = entry?.user;
    const participantName = participant?.displayName || "Brak imienia i nazwiska";
    const participantUniqueKey =
      `${entry?.uid || "no_uid"}__${participant?.index || participantName}__${participant?.dateOfBirth || ""}`;

    const age = getCurrentSchoolYearAge(participant?.dateOfBirth);
    if (age === null) return;

    const userGroups = Array.isArray(participant?.groups) ? participant.groups : [];
    if (!userGroups.length) return;

    const countedTypesForThisParticipant = new Set();
    let countedAtLeastOnce = false;

    userGroups.forEach((groupId) => {
      // liczymy tylko grupy, w których uczestnik WYSTĘPUJE na gali
      if (participant?.gala?.[groupId] !== true) return;

      const group = groupIndex.get(groupId);
      if (!group) return;

      countedAtLeastOnce = true;

      const typeName = detectTypeFromGroup(group);
      const participantTypeKey = `${participantUniqueKey}__${typeName}`;

      // 1 uczestnik liczony 1 raz w danym typie
      if (countedTypesForThisParticipant.has(participantTypeKey)) return;
      countedTypesForThisParticipant.add(participantTypeKey);

      const finalKey = `${age}__${typeName}`;

      if (!ageTypeMap.has(finalKey)) {
        ageTypeMap.set(finalKey, {
          age,
          type: typeName,
          count: 0,
        });
      }

      ageTypeMap.get(finalKey).count += 1;

      if (!groupedParticipants[age]) groupedParticipants[age] = {};
      if (!groupedParticipants[age][typeName]) groupedParticipants[age][typeName] = [];

      groupedParticipants[age][typeName].push({
        participantName,
        participantId: participantUniqueKey,
        dateOfBirth: participant?.dateOfBirth || "",
        groups: userGroups
          .filter((gid) => participant?.gala?.[gid] === true)
          .map((gid) => groupIndex.get(gid))
          .filter(Boolean)
          .map((g) => g?.name || g?.type || g?.id),
      });
    });

    if (countedAtLeastOnce) {
      globalUniqueParticipants.add(participantUniqueKey);
    }
  });

  const rows = Array.from(ageTypeMap.values()).sort((a, b) => {
    if (a.age !== b.age) return a.age - b.age;
    return String(a.type).localeCompare(String(b.type), "pl");
  });

  Object.keys(groupedParticipants).forEach((ageKey) => {
    Object.keys(groupedParticipants[ageKey]).forEach((typeKey) => {
      groupedParticipants[ageKey][typeKey].sort((a, b) =>
        String(a.participantName).localeCompare(String(b.participantName), "pl")
      );
    });
  });

  const countedPairs = rows.reduce((sum, row) => sum + row.count, 0);

  return {
    rows,
    grouped: groupedParticipants,
    totalParticipants: globalUniqueParticipants.size,
    countedPairs,
  };
}, [users, groups, groupIndex]);

  return (
    <div
      className={isToogle ? "side-toogle-margin" : "side-margin"}
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        minHeight: "100vh",
        overflowY: "auto",
        gap: "18px",
        paddingTop: "20px",
        paddingBottom: "30px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "column",
          gap: "18px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "24px", color: "rgb(30,30,30)" }}>
            Wiek uczestników według typów zajęć
          </h2>

          <div style={{ fontSize: "13px", color: "rgb(90,90,90)", fontWeight: "bold" }}>
            {loadingUsers || loadingGroups
              ? "Ładowanie..."
              : `Uczestnicy: ${stats.totalParticipants} | Zliczeń typów: ${stats.countedPairs}`}
          </div>
        </div>

        {!loadingUsers && !loadingGroups && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: "8px",
              backgroundColor: "rgba(0,69,199,0.05)",
              border: "1px solid rgba(0,69,199,0.15)",
              fontSize: "12px",
              color: "rgb(60,60,60)",
              lineHeight: "1.6",
            }}
          >
            Liczenie działa tak, że <b>1 uczestnik jest liczony tylko raz w danym typie zajęć</b>,
            nawet jeśli ma 2 grupy tego samego typu. Jeśli chodzi na różne typy zajęć,
            pojawi się w każdym odpowiednim typie osobno.
          </div>
        )}

        {loadingUsers || loadingGroups ? (
          <div
            style={{
              width: "100%",
              padding: "30px 20px",
              borderRadius: "8px",
              backgroundColor: "rgb(255,255,255,0.75)",
              boxShadow:
                "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
              textAlign: "center",
              color: "rgb(80,80,80)",
              fontWeight: "bold",
            }}
          >
            Ładowanie danych...
          </div>
        ) : stats.rows.length === 0 ? (
          <div
            style={{
              width: "100%",
              padding: "30px 20px",
              borderRadius: "8px",
              backgroundColor: "rgb(255,255,255,0.75)",
              boxShadow:
                "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
              textAlign: "center",
              color: "rgb(80,80,80)",
              fontWeight: "bold",
            }}
          >
            Brak danych do wyświetlenia.
          </div>
        ) : (
          <>
            <div
              style={{
                width: "100%",
                overflowX: "auto",
                backgroundColor: "rgb(255,255,255,0.78)",
                borderRadius: "10px",
                boxShadow:
                  "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
              }}
            >
              <table
                style={{
                  width: "100%",
                  minWidth: "700px",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyle}>Wiek</th>
                    <th style={thStyle}>Typ zajęć</th>
                    <th style={thStyle}>Ilość osób</th>
                    <th style={thStyle}>Opis</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.rows.map((row, index) => (
                    <tr key={`${row.age}_${row.type}_${index}`} style={{ borderTop: "1px solid rgba(0,0,0,0.07)" }}>
                      <td style={tdStyle}>{row.age} lat</td>
                      <td style={tdStyle}>{row.type}</td>
                      <td style={{ ...tdStyle, fontWeight: "bold", color: "#0045C7" }}>{row.count}</td>
                      <td style={tdStyle}>
                        {row.age} lat - {row.type}: {row.count} {row.count === 1 ? "osoba" : "osób"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "18px",
              }}
            >
              {Object.keys(stats.grouped)
                .map(Number)
                .sort((a, b) => a - b)
                .map((age) => (
                  <div
                    key={age}
                    style={{
                      backgroundColor: "rgb(255,255,255,0.78)",
                      borderRadius: "10px",
                      padding: "18px",
                      boxShadow:
                        "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "20px",
                        color: "rgb(30,30,30)",
                      }}
                    >
                      {age} lat
                    </h3>

                    {Object.keys(stats.grouped[age])
                      .sort((a, b) => a.localeCompare(b, "pl"))
                      .map((typeName) => (
                        <div
                          key={`${age}_${typeName}`}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            padding: "12px",
                            borderRadius: "8px",
                            backgroundColor: "rgba(0,0,0,0.03)",
                          }}
                        >
                          <h4
                            style={{
                              margin: 0,
                              fontSize: "16px",
                              color: "#0045C7",
                            }}
                          >
                            {typeName}: {stats.grouped[age][typeName].length}{" "}
                            {stats.grouped[age][typeName].length === 1 ? "osoba" : "osób"}
                          </h4>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            {stats.grouped[age][typeName].map((person) => (
                              <div
                                key={`${person.participantId}_${typeName}`}
                                style={{
                                  padding: "10px 12px",
                                  borderRadius: "6px",
                                  backgroundColor: "white",
                                  border: "1px solid rgba(0,0,0,0.06)",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "bold",
                                    color: "rgb(35,35,35)",
                                  }}
                                >
                                  {person.participantName}
                                </div>

                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "rgb(100,100,100)",
                                    marginTop: "4px",
                                  }}
                                >
                                  Data urodzenia: {person.dateOfBirth || "-"}
                                </div>

                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "rgb(100,100,100)",
                                    marginTop: "4px",
                                  }}
                                >
                                  Wszystkie grupy: {person.groups.join(", ")}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: "13px",
  color: "rgb(70,70,70)",
  borderBottom: "1px solid rgba(0,0,0,0.08)",
  backgroundColor: "rgba(0,0,0,0.02)",
};

const tdStyle = {
  padding: "12px 14px",
  fontSize: "13px",
  color: "rgb(35,35,35)",
  verticalAlign: "top",
};